import { PropsWithChildren } from "react";
import { FormattedMessage } from "react-intl";

import BlockAccordion from "@components/accordions/BlockAccordion";
import DateStamp from "@components/dates/DateStamp";
import DeleteConfirmationAlert from "@components/DeleteConfirmationAlert";
import styles from "@components/forms/careProfile/CareProfile.module.css";
import BodyCopy from "@components/typography/BodyCopy";
import useOpener from "@hooks/useOpener";

type Detail = {
  labelKey: string;
  value: Date | string | null;
};

type Props<DataType> = PropsWithChildren<{
  details: Detail[];
  item: DataType;
  onDelete?: (item: DataType) => void;
  onEdit: (item: DataType) => void;
  title: string;
}>;

const CareProfileRollupItem = <DataType extends unknown>({
  children,
  details,
  item,
  onDelete,
  onEdit,
  title
}: Props<DataType>) => {
  const deleteConfirmationOpener = useOpener();

  return (
    <>
      <BlockAccordion
        content={
          <>
            <div className={styles.rollupItemDetailsColumn}>
              {details
                .filter(d => d.value)
                .map(detail => (
                  <div className={styles.rollupItemDetailRow} key={detail.labelKey}>
                    <div className={styles.rollupItemDetailLabel}>
                      <BodyCopy bold>
                        <FormattedMessage id={detail.labelKey} />
                      </BodyCopy>
                    </div>
                    <div className={styles.rollupItemDetailValue}>
                      <BodyCopy>
                        {detail.value instanceof Date ? <DateStamp value={detail.value} /> : <>{detail.value}</>}
                      </BodyCopy>
                    </div>
                  </div>
                ))}
            </div>
            {children && <div className={styles.rollupItemDetailsChildrenWrapper}>{children}</div>}
            <div className={styles.rollupButtons}>
              <div className={styles.rollupEditButton} onClick={() => onEdit(item)}>
                <BodyCopy>
                  <FormattedMessage id="dictionary.edit" />
                </BodyCopy>
              </div>
              {onDelete && (
                <div className={styles.rollupDeleteButton} onClick={() => deleteConfirmationOpener.open()}>
                  <BodyCopy>
                    <FormattedMessage id="dictionary.delete" />
                  </BodyCopy>
                </div>
              )}
            </div>
          </>
        }
        heading={<BodyCopy color="gray-500">{title}</BodyCopy>}
      />
      <DeleteConfirmationAlert
        messageKey="pages.careProfile.deleteSubObjectMessage"
        onDelete={() => onDelete && onDelete(item)}
        opener={deleteConfirmationOpener}
      />
    </>
  );
};

export default CareProfileRollupItem;
